import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileBottomNav from '../../components/mobile/MobileBottomNav';
import MobileAdCard from '../../components/mobile/MobileAdCard';
import AdvancedSearch from '../../components/search/AdvancedSearch';
import SearchFacets from '../../components/search/SearchFacets';
import { NotificationCenter } from '../../components/common/NotificationCenter/NotificationCenter';
import { searchAds } from '../../services/adsApi';
import { useMarquees } from '../../hooks/useMarquees';
import styles from './MobileAdsListPage.module.css';
import type { Ad, AdvancedSearchParams, SearchFacetsData } from '../../types';
import type { Notification } from '../../services/notificationsApi';

type SortOption = 'comprehensive' | 'sales' | 'newest' | 'price';
type ViewMode = 'grid' | 'list';

export const MobileAdsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('comprehensive');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const { enabled: marqueeEnabled } = useMarquees();

  // Advanced search state
  const [searchFilters, setSearchFilters] = useState<AdvancedSearchParams>({
    sortBy: 'date',
    sortOrder: 'desc',
  });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showSearchFacets, setShowSearchFacets] = useState(false);
  const [facets, setFacets] = useState<SearchFacetsData | null>(null);

  // Check URL params on mount to auto-open advanced search
  useEffect(() => {
    const advancedParam = searchParams.get('advanced');
    if (advancedParam === 'true') {
      setShowAdvancedSearch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters, sortBy]);

  const fetchAds = async () => {
    setLoading(true);
    try {
      // Build search params for API
      const params: {
        category?: string;
        region?: string;
        keyword?: string;
        sortBy?: string;
        sortOrder?: string;
        limit?: number;
      } = {};

      // Map sortBy to API sortBy
      const sortByMapping: Record<string, string> = {
        'comprehensive': 'featured',
        'sales': 'popularity',
        'newest': 'date',
      };

      params.sortBy = sortByMapping[sortBy] || 'featured';
      params.sortOrder = searchFilters.sortOrder === 'asc' ? 'asc' : 'desc';
      params.limit = 50;

      if (searchFilters.categories && searchFilters.categories.length > 0) {
        params.category = searchFilters.categories[0];
      }
      if (searchFilters.regions && searchFilters.regions.length > 0) {
        params.region = searchFilters.regions[0];
      }
      if (searchFilters.keyword) {
        params.keyword = searchFilters.keyword;
      }

      const result = await searchAds(params);
      let filteredAds = result.data as Ad[];

      // Apply additional filters from searchFilters
      if (searchFilters.hasImage) {
        filteredAds = filteredAds.filter(ad => ad.imageUrls && ad.imageUrls.length > 0);
      }

      // Use facets from API response if available
      if (result.facets) {
        setFacets({
          categories: result.facets.categories || {},
          regions: result.facets.regions || {},
          priceRanges: result.facets.priceRanges || { '$': 0, '$$': 0, '$$$': 0, '$$$$': 0 },
        });
      } else {
        // Fallback: calculate facets locally if API doesn't provide them
        const mockFacets: SearchFacetsData = {
          categories: {
            '維修': filteredAds.filter(ad => ad.category === '維修').length,
            '保養': filteredAds.filter(ad => ad.category === '保養').length,
            '美容': filteredAds.filter(ad => ad.category === '美容').length,
            '改裝': filteredAds.filter(ad => ad.category === '改裝').length,
          },
          regions: {
            '台北市': filteredAds.filter(ad => ad.region === '台北市').length,
            '新北市': filteredAds.filter(ad => ad.region === '新北市').length,
            '桃園市': filteredAds.filter(ad => ad.region === '桃園市').length,
            '台中市': filteredAds.filter(ad => ad.region === '台中市').length,
            '台南市': filteredAds.filter(ad => ad.region === '台南市').length,
            '高雄市': filteredAds.filter(ad => ad.region === '高雄市').length,
          },
          priceRanges: {
            '$': 0,
            '$$': 0,
            '$$$': 0,
            '$$$$': 0,
          },
        };
        setFacets(mockFacets);
      }

      setAds(filteredAds);
    } catch (error) {
      console.error('Error fetching ads:', error);
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (option: SortOption) => {
    setSortBy(option);
  };

  const handleViewModeToggle = () => {
    setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
  };

  const handleAdClick = (ad: Ad) => {
    navigate(`/ads/${ad.id}`);
  };

  const handleSearch = () => {
    // Toggle advanced search panel and update URL
    const newState = !showAdvancedSearch;
    setShowAdvancedSearch(newState);
    if (newState) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('advanced', 'true');
        return newParams;
      });
    } else {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('advanced');
        return newParams;
      });
    }
  };

  const handleAdvancedSearch = (params: AdvancedSearchParams) => {
    setSearchFilters(params);
    setShowAdvancedSearch(false); // Close panel after search
    // Remove advanced parameter from URL when closing
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('advanced');
      return newParams;
    });
  };

  const handleFacetClick = (params: AdvancedSearchParams) => {
    setSearchFilters(params);
    setShowSearchFacets(false); // Close panel after selection
  };

  const handleNotificationClick = (notification: Notification) => {
    // Handle navigation based on notification type
    console.log('Notification clicked:', notification);
    // Could add navigation logic here based on notification type
  };

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    searchFilters.categories?.length ||
    searchFilters.regions?.length ||
    searchFilters.priceRange ||
    searchFilters.keyword ||
    searchFilters.hasImage
  );

  const handleClearFilters = () => {
    // Reset to default state
    setSearchFilters({
      sortBy: 'date',
      sortOrder: 'desc',
    });
    // Close filter panel if open
    if (showSearchFacets) {
      setShowSearchFacets(false);
    }
  };

  return (
    <div className={styles.mobileAdsListPage}>
      <MobileHeader
        title="廣告列表"
        showBack={false}
        showSearch
        showMarquee={marqueeEnabled}
        onSearch={handleSearch}
        rightAction={<NotificationCenter onNotificationClick={handleNotificationClick} />}
      />

      <div className={`${styles.mobileAdsListPage__content} ${marqueeEnabled ? styles['mobileAdsListPage__content--with-marquee'] : ''}`}>
        {/* Sorting Options */}
        <div className={styles.mobileAdsListPage__sortBar}>
          <div className={styles.mobileAdsListPage__sortOptions}>
            <button
              className={`${styles.mobileAdsListPage__sortOption} ${sortBy === 'comprehensive' ? styles['mobileAdsListPage__sortOption--active'] : ''}`}
              onClick={() => handleSortChange('comprehensive')}
            >
              綜合
            </button>
            <button
              className={`${styles.mobileAdsListPage__sortOption} ${sortBy === 'sales' ? styles['mobileAdsListPage__sortOption--active'] : ''}`}
              onClick={() => handleSortChange('sales')}
            >
              銷量
            </button>
            <button
              className={`${styles.mobileAdsListPage__sortOption} ${sortBy === 'newest' ? styles['mobileAdsListPage__sortOption--active'] : ''}`}
              onClick={() => handleSortChange('newest')}
            >
              上新
            </button>
          </div>
          <div className={styles.mobileAdsListPage__sortActions}>
            {hasActiveFilters && (
              <button
                className={styles.mobileAdsListPage__clearBtn}
                onClick={handleClearFilters}
                aria-label="清除篩選"
              >
                ✕
              </button>
            )}
            <button
              className={styles.mobileAdsListPage__filterBtn}
              onClick={() => setShowSearchFacets(!showSearchFacets)}
              aria-label="進階篩選"
            >
              🔍 篩選
            </button>
            <button
              className={styles.mobileAdsListPage__viewToggle}
              onClick={handleViewModeToggle}
              aria-label="切換檢視模式"
            >
              {viewMode === 'grid' ? '📋' : '⊞'}
            </button>
          </div>
        </div>

        {/* Advanced Search Panel (Collapsible) */}
        {showAdvancedSearch && (
          <div className={styles.mobileAdsListPage__advancedSearch}>
            <div className={styles.mobileAdsListPage__panelHeader}>
              <h3>進階搜尋</h3>
              <button
                className={styles.mobileAdsListPage__closePanelBtn}
                onClick={() => {
                  setShowAdvancedSearch(false);
                  setSearchParams((prev) => {
                    const newParams = new URLSearchParams(prev);
                    newParams.delete('advanced');
                    return newParams;
                  });
                }}
              >
                ✕
              </button>
            </div>
            <AdvancedSearch
              onSearch={handleAdvancedSearch}
              defaultParams={searchFilters}
            />
          </div>
        )}

        {/* Search Facets Panel (Collapsible) */}
        {showSearchFacets && facets && (
          <div className={styles.mobileAdsListPage__facetsPanel}>
            <div className={styles.mobileAdsListPage__panelHeader}>
              <h3>篩選條件</h3>
              <button
                className={styles.mobileAdsListPage__closePanelBtn}
                onClick={() => setShowSearchFacets(false)}
              >
                ✕
              </button>
            </div>
            <SearchFacets
              facets={facets}
              selectedParams={searchFilters}
              onFacetClick={handleFacetClick}
            />
          </div>
        )}

        {/* Product Grid/List */}
        <div className={styles.mobileAdsListPage__products}>
          {loading ? (
            <div className={styles.mobileAdsListPage__loading}>載入中...</div>
          ) : ads.length > 0 ? (
            <div className={`${styles.mobileAdsListPage__grid} ${viewMode === 'list' ? styles['mobileAdsListPage__grid--list'] : ''}`}>
              {ads.map((ad) => (
                <MobileAdCard
                  key={ad.id}
                  ad={ad}
                  variant={viewMode}
                  onClick={handleAdClick}
                />
              ))}
            </div>
          ) : (
            <div className={styles.mobileAdsListPage__empty}>
              <p>暫無廣告資料</p>
            </div>
          )}
        </div>
      </div>

      <MobileBottomNav />
    </div>
  );
};

export default MobileAdsListPage;
